/**
 * @module experience/shop/product-details
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { throwError as observableThrowError, Subject, Observable, Subscription } from 'rxjs';
import { COMPONENT_MAP_CONSTANTS } from '@core/aem/component-map.constants';
import { LoggerService } from '@core/logger/services/logger.service';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { APIError } from '@support/api/common/error.interface';

import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';

@Component({
  selector: 'tmo-related-products',
  templateUrl: './related-products.component.html',
  styleUrls: [],
})
export class RelatedProductsComponent implements OnInit, OnDestroy {
  public pdpStaticContent: ConfigData;
  public subscription: Subscription;
  /**
   * Next. Complete. Unsubscribe.
   */
  private ngUnsubscribe: Subject<void>;
  constructor(
    private router: Router,
    private contentProviderService: ContentProviderService,
    private logger: LoggerService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  public ngOnInit(): void {
    this.logger.debug(
      COMPONENT_MAP_CONSTANTS.AEM_MODULE,
      COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
      'entering ngOnInit() in ' + COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT
    );
    this.subscription = this.getProductDetailsContent()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((content: ConfigData) => {
        this.logger.debug(
          COMPONENT_MAP_CONSTANTS.AEM_MODULE,
          COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
          'entering ngOnInit() in ' +
            COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT +
            ' getProductDetailsContent response: ' +
            JSON.stringify(content)
        );
        this.pdpStaticContent = content;
      });
    this.logger.debug(
      COMPONENT_MAP_CONSTANTS.AEM_MODULE,
      COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
      'leaving ngOnInit() in ' + COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT
    );
  }

  public moveToRoute(event: string): void {
    this.router.navigate([event]);
  }

  public ngOnDestroy(): void {
    this.logger.debug(
      COMPONENT_MAP_CONSTANTS.AEM_MODULE,
      COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
      'entering ngOnDestroy() in ' + COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT
    );
    this.subscription.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.logger.debug(
      COMPONENT_MAP_CONSTANTS.AEM_MODULE,
      COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
      'leaving ngOnDestroy() in ' + COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT
    );
  }

  /**
   * gets the pdp static content
   * @returns Observable
   */
  public getProductDetailsContent(): Observable<object> {
    this.logger.debug(
      COMPONENT_MAP_CONSTANTS.AEM_MODULE,
      COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
      'leaving getProductDetailsContent() in ' + COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT
    );
    return this.contentProviderService.getContentData(CONTENT_ENDPOINT.PRODUCT_DETAILS).pipe(
      map((contentData: ConfigData) => {
        this.logger.debug(
          COMPONENT_MAP_CONSTANTS.AEM_MODULE,
          COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
          'entering ngOnInit() in ' +
            COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT +
            ' getContentData response: ' +
            JSON.stringify(contentData)
        );
        return this.contentProviderService.getConfigurableData(contentData);
      }),
      catchError((error: APIError) => {
        this.logger.error(
          COMPONENT_MAP_CONSTANTS.AEM_MODULE,
          COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT,
          ' API error for postRequest getProductDetailsContent() in ' +
            COMPONENT_MAP_CONSTANTS.RELATED_PRODUCTS_COMPONENT +
            ' error: ' +
            JSON.stringify(error),
          error.code
        );
        return observableThrowError(error);
      })
    );
  }
}
